import { useEffect, useState } from "react";
import { Search, Loader2, Plus } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { estApi } from "../lib/api";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;
const TABS = [
  { key: "items", label: "Cost library items" },
  { key: "groups", label: "Groups" },
  { key: "blank", label: "Blank line" },
];

export default function AddLinesDialog({ open, onOpenChange, onAdd, sections, onNewItem }) {
  const [tab, setTab] = useState("items");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [picked, setPicked] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [qty, setQty] = useState("1");
  const [section, setSection] = useState("");
  const [kind, setKind] = useState("material");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked([]);
    setGroupId("");
  }, [open]);

  useEffect(() => {
    if (!open || tab === "blank") return;
    setLoading(true);
    const run = tab === "items"
      ? estApi.getPriceList({ search, active: true, limit: 60, sort: "name" }).then((d) => setItems(d.items || []))
      : estApi.getGroups({ search, active: true }).then((d) => setGroups(d.groups || []));
    run.finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [open, tab, search]);

  const submit = async () => {
    const body = { section, qty: parseFloat(qty) || 1 };
    if (tab === "items") body.price_list_item_ids = picked;
    if (tab === "groups") body.group_id = groupId;
    if (tab === "blank") { body.blank = true; body.line_kind = kind; }
    setSaving(true);
    try {
      await onAdd(body);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = tab === "blank" || (tab === "items" ? picked.length > 0 : !!groupId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="add-lines-dialog">
        <DialogHeader><DialogTitle>Add lines to this estimate</DialogTitle></DialogHeader>

        <div className="flex gap-1 border-b border-slate-200">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => { setTab(t.key); if (t.key === "blank") setQty("1"); }}
                    className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${
                      tab === t.key ? "border-[#007BFF] text-[#007BFF]"
                        : "border-transparent text-slate-500 hover:text-slate-800"}`}
                    data-testid={`add-lines-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab !== "blank" && (
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search..."
                   className="h-8 pl-8 text-xs" data-testid="add-lines-search" />
          </div>
        )}

        <div className="max-h-72 overflow-y-auto rounded border border-slate-200">
          {loading && (
            <div className="flex items-center justify-center py-8 text-xs text-slate-400">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && tab === "items" && (
            <table className="w-full text-xs" data-testid="add-lines-items-table">
              <tbody>
                {items.length === 0 && (
                  <tr><td className="px-3 py-8 text-center text-slate-400">No items found.</td></tr>
                )}
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50"
                      data-testid={`add-item-row-${it.id}`}>
                    <td className="w-8 px-2 py-1.5">
                      <input type="checkbox" checked={picked.includes(it.id)}
                             onChange={() => setPicked((p) =>
                               p.includes(it.id) ? p.filter((x) => x !== it.id) : [...p, it.id])}
                             data-testid={`add-item-check-${it.id}`} />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-slate-500">{it.item_number || "—"}</td>
                    <td className="px-2 py-1.5 text-slate-800">{it.name}</td>
                    <td className="px-2 py-1.5 text-slate-400">{it.cost_code || "no code"}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {it.has_cost ? money(it.unit_cost)
                        : <span className="rounded bg-amber-100 px-1.5 text-[10px] text-amber-700">Quote</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && tab === "groups" && (
            <table className="w-full text-xs" data-testid="add-lines-groups-table">
              <tbody>
                {groups.length === 0 && (
                  <tr><td className="px-3 py-8 text-center text-slate-400">No groups found.</td></tr>
                )}
                {groups.map((g) => (
                  <tr key={g.id}
                      className={`cursor-pointer border-b border-slate-100 hover:bg-slate-50 ${
                        groupId === g.id ? "bg-[#007BFF]/5" : ""}`}
                      onClick={() => setGroupId(g.id)} data-testid={`add-group-row-${g.id}`}>
                    <td className="w-8 px-2 py-1.5">
                      <input type="radio" checked={groupId === g.id} onChange={() => setGroupId(g.id)} />
                    </td>
                    <td className="px-2 py-1.5 text-slate-800">{g.name}</td>
                    <td className="px-2 py-1.5 text-slate-400">{g.item_count} items</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{money(g.total_unit_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {tab === "blank" && (
            <div className="p-4">
              <label className="text-[11px] text-slate-500">Line type</label>
              <select value={kind} onChange={(e) => setKind(e.target.value)}
                      className="mt-1 h-8 w-full rounded border border-slate-200 px-2 text-xs"
                      data-testid="blank-line-kind">
                <option value="material">Material (qty × unit cost + labor hours)</option>
                <option value="labor_standalone">Labor only (qty = hours, unit cost = $/hr)</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-[11px] text-slate-500">Section (optional)</label>
            <Input value={section} onChange={(e) => setSection(e.target.value)}
                   list="estimate-sections" placeholder="e.g. Restroom 101"
                   className="h-8 text-xs" data-testid="add-lines-section" />
            <datalist id="estimate-sections">
              {(sections || []).map((s) => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div className="w-24">
            <label className="text-[11px] text-slate-500">Qty</label>
            <Input value={qty} type="number" onChange={(e) => setQty(e.target.value)}
                   className="h-8 text-right text-xs" data-testid="add-lines-qty" />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          {tab === "items" && onNewItem ? (
            <Button variant="outline" size="sm" className="text-xs"
                    onClick={() => onNewItem(section)} data-testid="add-lines-new-item">
              <Plus className="mr-1.5 h-3.5 w-3.5" /> New library item
            </Button>
          ) : <span />}
          <div className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="add-lines-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || saving} data-testid="add-lines-submit">
            {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Add {tab === "items" && picked.length > 0 ? `${picked.length} item(s)` : "line(s)"}
          </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
